import { useMutation } from '@tanstack/react-query'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { useLocalizedSignIn } from '../../hooks/useLocalizedSignIn'
import { ValidationError } from '../../libs/next/api/jsonApiHandler'
import { CheckboxRegular } from '../components/form/CheckboxRegular'
import { ComboboxInput } from '../components/form/ComboboxInput'
import { TextInput } from '../components/form/TextInput'
import { Button } from '../components/shared/Button'
import { InfoModal } from '../components/shared/InfoModal'
import { RegisterSchema } from '../components/shared/RegisterSchema'
import { InternalLink } from '../const/Links'
import { LARGE_SELECT_HEIGHT } from '../const/digitContants'
import { Translation, useTranslate } from '../contexts/TranslationContext'
import style from '../styles/LoginRegisterPage.module.sass'
import SvgPasswordNotVisible from '../svg/icons/PasswordNotVisible'
import SvgPasswordVisible from '../svg/icons/PasswordVisible'
import { useActiveDog } from './ActiveDogContext'
import { SignUpInput } from './SignUpInput'
import { useAuth } from './UserContext'

type NullableValues<R extends Record<string, unknown>> = {
	[K in keyof R]: R[K] | null
}

type NullableRegisterSchema = NullableValues<RegisterSchema>

export function SignUpform() {
	const auth = useAuth()
	const translate = useTranslate()

	const eBookOptions = [
		{
			id: 'adult',
			label: translate('ebook.for.male'),
		},
		{
			id: 'puppyFemale',
			label: translate('ebook.for.femalePuppy'),
		},
		{
			id: 'senior',
			label: translate('ebook.for.senior'),
		},
		{
			id: 'puppy',
			label: translate('ebook.for.puppy'),
		},
	]

	const [isSelectVisible, setIsSelectVisible] = useState(false)
	const [emailIsTaken, setEmailIsTaken] = useState(false)
	const [wantNewsletter, setWantNewsletter] = useState(false)
	const [freeBook, setFreeBook] = useState(false)
	const [password, setPassword] = useState<string | null>(null)
	const [email, setEmail] = useState<string | null>('')
	const [option, setOption] = useState<string | null>(eBookOptions[0].id)
	const [passwordType, setPasswordType] = useState<'password' | 'text'>('password')
	const [passwordAlertVisible, setPasswordAlertVisible] = useState(false)
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
	const [missingPasswordAlertVisible, setMissingPasswordAlertVisible] = useState(false)
	const [wrongFormatAlertVisible, setWrongFormatAlertVisible] = useState(false)

	const togglePassword = () => {
		if (passwordType === 'password') {
			setPasswordType('text')
			return
		}
		setPasswordType('password')
	}

	const schema = useMemo<NullableRegisterSchema>(
		() => ({
			email,
			password,
		}),
		[email, password],
	)

	const registerInfo = useMemo(() => {
		return RegisterSchema.safeParse({ ...schema })
	}, [schema])

	const { push, query } = useRouter()

	const onChange = (event: ChangeEvent<HTMLInputElement>) => {
		setIsSelectVisible(event.currentTarget.checked)
		setFreeBook(!freeBook)
	}

	useEffect(() => {
		if (typeof window != 'undefined' && window.document && isConfirmModalOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = 'auto'
		}
	}, [isConfirmModalOpen])

	const redirect = () => {
		const redirectTo: string = query?.callbackUrl ? (query.callbackUrl as string) : InternalLink.Home

		push(redirectTo)
	}

	const checkEmail = (value: string) => {
		const reg1 = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/
		if (value !== '' && reg1.test(value) == false) {
			setWrongFormatAlertVisible(true)
		}
	}

	const checkPasswordLength = (value: string) => {
		if (value.length > 0 && value.length < 6) {
			setPasswordAlertVisible(true)
		}
	}
	const { signIn } = useLocalizedSignIn()
	const { saveActiveDog } = useActiveDog()
	const { dog } = useActiveDog()

	const signUpMutation = useMutation({
		mutationFn: async (formData: FormData) => {
			const data = Object.fromEntries([...formData.entries()])

			const validData = SignUpInput.parse({
				...data,
				eBookVariant: isSelectVisible ? option : null,
				joinNewsletter: wantNewsletter,
			})

			try {
				const signUp = await auth.signUp(validData)
				if (signUp?.status === 'ok') {
					setIsConfirmModalOpen(true)
					if (dog) {
						saveActiveDog(dog, signUp.userId)
					}
				}
			} catch (e) {
				if (e instanceof ValidationError) {
					setEmailIsTaken(true)
				}
			}
		},
	})

	return (
		<>
			{isConfirmModalOpen && (
				<InfoModal
					title={translate('signUpForm.modal.confirm.title')}
					lead={translate('signUpForm.modal.confirm.subtitle')}
					imageUrl="/images/pes_modal_3.jpg"
					buttonLabel={translate('signUpForm.modal.confirm.button')}
					buttonType="button"
					isOpen={isConfirmModalOpen}
					imageAlternativeText={translate('signUpForm.modal.alt.image')}
					onClick={() => signIn()}
					closeModal={redirect}
				/>
			)}
			<form
				action=""
				method="POST"
				onSubmit={async (e) => {
					e.preventDefault()

					if (!password) {
						setMissingPasswordAlertVisible(true)
						return
					}

					if (registerInfo.success) {
						const formData = new FormData(e.currentTarget)
						await signUpMutation.mutateAsync(formData)
					} else {
						setPasswordAlertVisible(true)
					}
				}}
			>
				<fieldset>
					<div className={style.inputGroupWrapper}>
						<div className={style.inputWrapper}>
							<TextInput
								name="email"
								type="email"
								value={email ?? ''}
								onChange={(e) => setEmail(e.target.value)}
								placeholder={translate('email.placeholder')}
								autoComplete="email"
								onBlur={(e) => checkEmail(e.target.value)}
								onFocus={() => {
									setWrongFormatAlertVisible(false)
									setEmailIsTaken(false)
								}}
							/>
							{emailIsTaken && (
								<span className={style.textInputAlert}>
									<Translation tr="infoModal.forgotPassword.account.exists.alert" />
								</span>
							)}
							{wrongFormatAlertVisible && (
								<span className={style.textInputAlert}>
									<Translation tr="infoModal.forgotPassword.wrong.format.alert" />
								</span>
							)}
						</div>
						<div className={style.textInputWrapper}>
							<TextInput
								name="password"
								onFocus={() => {
									setPasswordAlertVisible(false)
									setMissingPasswordAlertVisible(false)
								}}
								type={passwordType}
								value={password ?? ''}
								onBlur={(e) => checkPasswordLength(e.target.value)}
								onChange={(e) => setPassword(e.currentTarget.value)}
								placeholder={translate('signUpForm.password.placeholder')}
								autoComplete="new-password"
								icon={
									<button
										type="button"
										onClick={togglePassword}
										className={style.buttonPassword}
										aria-label={
											passwordType === 'password'
												? translate('signInForm.email.password.button.show')
												: translate('signInForm.email.password.button.hide')
										}
									>
										{passwordType === 'password' ? (
											<span className={style.passwordIcon}>
												<SvgPasswordVisible />
											</span>
										) : (
											<span className={clsx(style.passwordIcon, style.passwordIconVisible)}>
												<SvgPasswordNotVisible />
											</span>
										)}
									</button>
								}
							/>
							{missingPasswordAlertVisible && (
								<span className={style.textInputAlert}>
									<Translation tr="signUpForm.password.missing.alert" />
								</span>
							)}
							{passwordAlertVisible && (
								<span className={style.textInputAlert}>
									<Translation tr="signUpForm.password.alert" />
								</span>
							)}
						</div>
					</div>
					<div className={style.checkboxGroup}>
						<CheckboxRegular
							name="wantNewsletter"
							label={translate('signUpForm.newsletter.title')}
							checked={wantNewsletter}
							onChange={() => setWantNewsletter(!wantNewsletter)}
						/>
						<CheckboxRegular
							name="freeBook"
							label={translate('signUpForm.ebook.title')}
							onChange={onChange}
							checked={freeBook}
						/>
						{isSelectVisible && (
							<div className={style.selectWrapper}>
								<ComboboxInput
									name="ebook"
									state={[option, setOption] as const}
									searchPlaceholder={translate('ebook.placeholder')}
									items={eBookOptions ?? []}
									height={LARGE_SELECT_HEIGHT}
									isNotSearchable
								/>
							</div>
						)}
					</div>
					<div className={style.buttonWrapper}>
						<Button
							buttonType="submit"
							label={
								signUpMutation.isPending ? (
									<span className={style.loaderBody}>
										<Translation tr="signUpForm.button" />
										<span className={style.loader} />
									</span>
								) : (
									translate('signUpForm.button')
								)
							}
							disabled={wrongFormatAlertVisible || passwordAlertVisible}
						/>
					</div>
				</fieldset>
			</form>
		</>
	)
}
